// frontend/src/pages/club/ClubPlayers/ClubPlayers.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import ExportButton from '../../../components/common/ExportButton';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import { getPlayersApi, deletePlayerApi, exportPlayersApi } from '../../../api/player.api';
import { AGE_GROUP_LABELS, GENDER_LABELS, PLAYER_STATUS_LABELS } from '../../../utils/constants';
import PlayerForm from '../../../components/forms/PlayerForm';
import PlayersTable from '../../../components/tables/PlayersTable';

export default function ClubPlayers() {
  const [players, setPlayers] = useState([]);
  const [filters, setFilters] = useState({ ageGroup: '', gender: '', status: '', search: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getPlayersApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setPlayers(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت بازیکنان');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePlayerApi(deleteTarget);
      setSnackbar('بازیکن غیرفعال شد');
      setDeleteTarget(null);
      loadPlayers();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">بازیکنان باشگاه</h1>
          <p className="mt-1 text-xs text-text-dark/50">
            بازیکنان جدید تا تایید مدیر سیستم در وضعیت «در انتظار تایید» نمایش داده می‌ شوند.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton fileName="players.xlsx" onExport={() => exportPlayersApi({})} />
          <Button variant="primary" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Icon name="plus" className="ml-2 h-4 w-4" /> افزودن بازیکن
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.ageGroup}
          onChange={(e) => setFilters({ ...filters, ageGroup: e.target.value })}
        >
          <option value="">همه گروه‌ها</option>
          {Object.entries(AGE_GROUP_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.gender}
          onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
        >
          <option value="">همه جنسیت‌ ها</option>
          {Object.entries(GENDER_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(PLAYER_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <Input
          placeholder="جستجو..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="h-11!"
        />
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        {loading ? (
          <div className="py-8 text-center text-text-dark/50">در حال بارگذاری...</div>
        ) : (
          <>
            <PlayersTable
              players={players}
              showClub={false}
              onEdit={(p) => { setEditing(p); setFormOpen(true); }}
              onDelete={(id) => setDeleteTarget(id)}
              onRefresh={loadPlayers}
              canManageStatus={false}
            />
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <PlayerForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={loadPlayers}
        player={editing}
        clubs={[]}        // مدیر باشگاه نیازی به انتخاب باشگاه ندارد
        isAdmin={false}   // نقش مدیر باشگاه
      />

      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="تأیید غیرفعال‌ سازی">
        <p className="text-text-dark/70">آیا از غیرفعال‌ سازی این بازیکن مطمئن هستید؟</p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>انصراف</Button>
          <Button variant="primary" onClick={handleDelete}>تأیید</Button>
        </div>
      </Modal>
    </div>
  );
}